/**
 * Controlled modal for editing one SKU's price. Client-side validation mirrors the
 * setSkuPrice Zod contract (price >= 0 required; compareAtPrice >= 0 or blank -> null).
 * On submit it calls onSave({ price, compareAtPrice }); the parent owns the mutation so
 * this stays presentational. Esc / backdrop close; focus trapped to the dialog.
 */
import { useEffect, useId, useRef, useState } from 'react';
import './priceEditModal.css';

export interface PriceEditValues {
  price: number;
  compareAtPrice: number | null;
}

interface PriceEditModalProps {
  skuCode: string;
  /** Current DB price if known (listSkus exposes it post follow-up #1); null -> show "—". */
  currentPrice?: number | null;
  currentCompareAt?: number | null;
  saving?: boolean;
  onSave: (values: PriceEditValues) => void;
  onClose: () => void;
}

function parseMoney(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN as unknown as number; // NaN flags an invalid entry
}

export function PriceEditModal({
  skuCode,
  currentPrice,
  currentCompareAt,
  saving = false,
  onSave,
  onClose,
}: PriceEditModalProps) {
  const [price, setPrice] = useState(currentPrice != null ? String(currentPrice) : '');
  const [compareAt, setCompareAt] = useState(currentCompareAt != null ? String(currentCompareAt) : '');
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  // Focus the first field on open; restore focus to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    firstFieldRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // Esc to close + simple focus trap within the dialog.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const priceNum = parseMoney(price);
    if (priceNum == null || Number.isNaN(priceNum)) {
      setError('Enter a price (a number ≥ 0).');
      return;
    }
    if (priceNum < 0) {
      setError('Price cannot be negative.');
      return;
    }
    const compareNum = parseMoney(compareAt);
    if (compareNum != null && (Number.isNaN(compareNum) || compareNum < 0)) {
      setError('Compare-at must be a number ≥ 0, or left blank.');
      return;
    }
    setError(null);
    onSave({ price: priceNum, compareAtPrice: compareNum });
  }

  const dash = (v: number | null | undefined) => (v != null ? `$${v.toFixed(2)}` : '—');

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="modal__title">
          Edit price — <span className="modal__sku">{skuCode}</span>
        </h2>
        <p className="modal__current">
          Current: {dash(currentPrice)}
          {currentCompareAt != null && <> · compare-at {dash(currentCompareAt)}</>}
        </p>

        <form onSubmit={handleSubmit} className="modal__form">
          <label className="modal__field">
            <span className="modal__label">Price (USD)</span>
            <input
              ref={firstFieldRef}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="modal__input"
              required
            />
          </label>

          <label className="modal__field">
            <span className="modal__label">
              Compare-at (USD) <span className="modal__optional">— optional</span>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
              className="modal__input"
              placeholder="Leave blank to clear"
            />
          </label>

          {error && (
            <p className="modal__error" role="alert">
              {error}
            </p>
          )}

          <div className="modal__actions">
            <button type="button" className="modal__btn modal__btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="modal__btn modal__btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save price'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
